import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { ClientCredentials } from 'src/app/components/content-oauth/services/oauth.service';
import { ComponentUtilsService } from './component-utils.service';
import { environment } from 'src/environments/environment';

export type DomoValidationStatus = {
  status:
    | 'UNCHECKED'
    | 'INTERNAL_ERROR'
    | 'INVALID_SOURCE'
    | 'INVALID_CREDENTIALS'
    | 'INVALID_FILTERS'
    | 'MISSING_CREDENTIALS'
    | 'ACCOUNT_MISMATCH'
    | 'VALID';
  isPrivate?: boolean;
};

export interface DomoFilter {
  column: string;
  operator: string;
  values: string[];
}

const DOMO_PROVIDER = 'domo';
const ENDPOINT_TOKEN = 'token';

@Injectable({
  providedIn: 'root',
})
export class DomoService {
  private static readonly PROXY_URL = 'https://services2.risevision.com/proxy';

  constructor(
    private httpClient: HttpClient,
    private componentUtilsService: ComponentUtilsService,
  ) {}

  async getSignature(companyId: string): Promise<string> {
    try {
      return await this.componentUtilsService.getSignature(
        companyId,
        DOMO_PROVIDER,
      );
    } catch (error) {
      console.error('Domo signature request call error', error);
      throw Error('INTERNAL_ERROR');
    }
  }

  async validateSourceAndCredentials(
    source: string,
    credentials: ClientCredentials,
    companyId: string,
    signature: string,
    filters: string,
  ): Promise<DomoValidationStatus> {
    if (!companyId) {
      return { status: 'INTERNAL_ERROR' };
    }

    if (!source) {
      return { status: 'INVALID_SOURCE' };
    }

    if (source.indexOf('://') === -1 || source.indexOf('://') > 5) {
      source = 'https://' + source;
    }

    try {
      new URL(source);
    } catch (error) {
      return { status: 'INVALID_SOURCE' };
    }

    const domoInfo = this._extractDomoInfoFromSrc(source);
    if (
      !domoInfo ||
      (domoInfo.assetType !== 'card' && domoInfo.assetType !== 'pages')
    ) {
      return { status: 'INVALID_SOURCE' };
    }

    const isPrivate = domoInfo.visibility === 'private';
    if (!credentials && (isPrivate || filters)) {
      return { status: 'MISSING_CREDENTIALS', isPrivate };
    }

    if (!isPrivate && !filters) {
      try {
        await this.validatePublicContent(source);
        return { status: 'VALID', isPrivate };
      } catch (error) {
        if (error instanceof Error) {
          return {
            status: error.message as DomoValidationStatus['status'],
            isPrivate,
          };
        }
        throw error;
      }
    } else {
      try {
        await this.validatePrivateContent(
          source,
          companyId,
          signature,
          filters,
        );
        return { status: 'VALID', isPrivate };
      } catch (error) {
        if (error instanceof Error) {
          return {
            status: error.message as DomoValidationStatus['status'],
            isPrivate,
          };
        }
        throw error;
      }
    }
  }

  private async validatePublicContent(src: string): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.httpClient.get(`${DomoService.PROXY_URL}/${src}`, {
          headers: {
            'X-Requested-With': 'rise-domo',
          },
          observe: 'response',
          responseType: 'text',
        }),
      );

      if (!response.headers.get('Report-To')) {
        throw Error('INVALID_SOURCE');
      }
    } catch (error) {
      console.error('Domo public content request call error', error);
      throw error;
    }
  }

  private async validatePrivateContent(
    source: string,
    companyId: string,
    signature: string,
    filters?: string,
  ): Promise<void> {
    const tokenUrl = environment.DATA_SERVICE_URL + ENDPOINT_TOKEN;
    const body: {
      account: string;
      provider: string;
      signature: string;
      additionalData: {
        source: string;
        filters?: string;
      };
    } = {
      account: companyId,
      provider: DOMO_PROVIDER,
      signature,
      additionalData: {
        source,
      },
    };

    if (filters) {
      body.additionalData.filters = filters;
    }

    try {
      const embedTokenResponse = await lastValueFrom(
        this.httpClient.post<{ access_token: string }>(tokenUrl, body, {
          headers: {
            'Content-Type': 'application/json',
          },
        }),
      );
      if (!embedTokenResponse?.access_token) {
        throw { error: 'INTERNAL_ERROR' };
      }
    } catch (error) {
      if (error.error) {
        throw Error(error.error);
      }
      console.error('Domo embed token request call error', error);
      throw error;
    }
  }

  private _extractDomoInfoFromSrc(src: string) {
    if (!src || typeof src !== 'string') {
      return false;
    }

    // Regex pattern to match: https://{{instanceName}}.domo.com/embed/{{assetType}}/{{visibility='private'}}/{{embedId}}
    // or: https://{{instanceName}}.domo.com/embed/{{assetType}}/{{embedId}}
    // Capture groups:
    // 1. instanceName - alphanumeric and hyphens
    // 2. assetType - alphanumeric and hyphens
    // 3. visibility - optional, must be 'private' if present (otherwise omitted for 'public')
    // 4. embedId - alphanumeric, hyphens, and underscores
    const domoUrlPattern =
      /^https:\/\/([a-zA-Z0-9-]+)\.domo\.com\/embed\/([a-zA-Z0-9-]+)\/(?:(private)\/)?([a-zA-Z0-9_-]+)$/;

    const match = src.match(domoUrlPattern);

    if (!match) {
      return false;
    }

    // Return an object with the captured groups for further use
    // visibility is 'private' if explicitly included, 'public' if omitted
    const domoInfo = {
      instanceName: match[1],
      assetType: match[2],
      visibility: match[3] === 'private' ? 'private' : 'public',
      embedId: match[4],
    };

    return domoInfo;
  }
}
